import { ListResult, RecordListOptions, RecordModel } from 'pocketbase';
import {
  IConfirmedExchangeDetails,
  IPublicAvailableSwap,
  IPublicAvailableSwapDetails,
} from '../interfaces/exchangeIntention';
import { ISearchBy, SwapsSort } from '../interfaces/search';
import { PocketBaseService } from './pocketbase.service';
import { utilService } from './util.service';

export const exchangeService = {
  loadExchangeIntentions,
  loadExchangeById,
  loadConfirmedExchangeDetails,
  getDefaultSearch,
  getSearchFromParams,
  getDefaultFilter,
};

const pb = PocketBaseService.getInstance().getPocketBase();

function escapeFilterValue(value: string): string {
  return value.replace(/'/g, "\\'");
}

function mapRecordModelToExchangeIntentions(data: ListResult<RecordModel>): IPublicAvailableSwap[] {
  return data.items.map((item) => mapRecordModelToExchangeIntention(item));
}

function mapRecordModelToExchangeIntention(data: RecordModel): IPublicAvailableSwap {
  return {
    _id: data.id,
    city: data.city,
    state: data.state,
    intended_destination: data.intended_destination,
    suggested_start_date: data.suggested_start_date,
    suggested_end_date: data.suggested_end_date,
    pictures: data.pictures,
    user_id: data.user_id,
    average_rating: data.average_rating,
    rating_count: data.rating_count,
  };
}

function mapRecordModelToExchangeIntentionDetails(data: RecordModel): IPublicAvailableSwapDetails {
  return {
    _id: data.id,
    city: data.city,
    state: data.state,
    intended_destination: data.intended_destination,
    suggested_start_date: data.suggested_start_date,
    suggested_end_date: data.suggested_end_date,
    pictures: data.pictures,
    amenities: data.amenities,
    residence_description: data.residence_description,
    user_id: data.user_id,
    user_name: data.user_name,
    user_avatar_url: data.user_avatar_url,
    user_identity_verified: data.user_identity_verified ?? false,
    average_rating: data.average_rating,
    rating_count: data.rating_count,
  };
}

function mapRecordModelToConfirmedExchangeDetails(data: RecordModel): IConfirmedExchangeDetails {
  if (!data) {
    throw new Error('Record data is missing');
  }

  const residenceInfo = data.residence_info || {};

  const user = residenceInfo.user || {};

  return {
    _id: data.id || '',
    start_date: data.start_date,
    end_date: data.end_date,
    pictures: data.pictures || [],
    amenities: data.amenities || [],
    residence_info: {
      id: residenceInfo.id || '',
      address: residenceInfo.address || '',
      description: residenceInfo.description || '',
      amenities: residenceInfo.amenities || [],
      city: residenceInfo.city || '',
      state: residenceInfo.state || '',
      lat: residenceInfo.lat || 0,
      lon: residenceInfo.lon || 0,
      pictures: residenceInfo.pictures || [],
      user: {
        id: user.id || '',
        avatar_url: user.avatar_url || '',
        name: user.name || '',
        phone_number: user.phone_number || '',
        identity_verified: user.identity_verified ?? false,
      },
    },
  };
}

function buildWantsMyCityFilter(city: string): string {
  const safe = escapeFilterValue(city);
  // Destinations are stored as "City", "City, State", or "anywhere".
  return (
    `(intended_destination != 'anywhere' && intended_destination != ''` +
    ` && (intended_destination = '${safe}'` +
    ` || intended_destination ~ '${safe},'` +
    ` || intended_destination ~ ', ${safe}'))`
  );
}

async function loadExchangeIntentions(
  searchBy: ISearchBy = getDefaultSearch(),
  pageIdx = 0
): Promise<{ exchanges: IPublicAvailableSwap[]; totalPages: number; totalItems: number }> {
  try {
    const perPage = 20; // Fixed per page count
    const page = pageIdx + 1; // Convert 0-based to 1-based for PocketBase

    const listOptions: RecordListOptions = {
      page,
      perPage,
    };

    const filters: string[] = [];
    if (searchBy.city) {
      filters.push(`city='${escapeFilterValue(searchBy.city)}'`);
    }
    if (searchBy.state) {
      filters.push(`state='${escapeFilterValue(searchBy.state)}'`);
    }

    // Date filters: match listings whose available range intersects the requested date(s)
    // Intersection logic:
    // - If both dates provided: itemStart <= searchEnd AND itemEnd >= searchStart
    // - If only start provided: itemStart <= searchStart AND itemEnd >= searchStart
    // - If only end provided:   itemStart <= searchEnd AND itemEnd >= searchEnd
    if (searchBy.startDate && searchBy.endDate) {
      const start = formatDate(searchBy.startDate);
      const end = formatDate(searchBy.endDate);
      filters.push(`(suggested_start_date<='${end}' && suggested_end_date>='${start}')`);
    } else if (searchBy.startDate) {
      const point = formatDate(searchBy.startDate);
      filters.push(`(suggested_start_date<='${point}' && suggested_end_date>='${point}')`);
    } else if (searchBy.endDate) {
      const point = formatDate(searchBy.endDate);
      filters.push(`(suggested_start_date<='${point}' && suggested_end_date>='${point}')`);
    }

    if (searchBy.wantsMyCity && searchBy.myResidenceCity?.trim()) {
      filters.push(buildWantsMyCityFilter(searchBy.myResidenceCity.trim()));
    }

    if (filters.length) {
      listOptions.filter = `(${filters.join(' && ')})`;
    }

    if (searchBy.sort === 'soonest') {
      listOptions.sort = 'suggested_start_date';
    }

    const exIntentions = await pb
      .collection('pub_available_swaps')
      .getList(page, perPage, listOptions);
    const exchanges = mapRecordModelToExchangeIntentions(exIntentions);
    const totalPages = exIntentions.totalPages;

    return { exchanges, totalPages, totalItems: exIntentions.totalItems };
  } catch (err) {
    return { exchanges: [], totalPages: 0, totalItems: 0 };
  }
}

async function loadExchangeById(exchangeId: string): Promise<IPublicAvailableSwapDetails | null> {
  const exchange = await pb.collection('pub_available_swap_details').getOne(exchangeId, {
    sort: 'id',
  });
  if (!exchange) {
    return null;
  }
  return mapRecordModelToExchangeIntentionDetails(exchange);
}

async function loadConfirmedExchangeDetails(
  exchangeId: string
): Promise<IConfirmedExchangeDetails | null> {
  const exchange = await pb.send(`/api/confirmed-exchange-details/${exchangeId}`, {
    method: 'GET',
  });
  if (!exchange) {
    return null;
  }
  return mapRecordModelToConfirmedExchangeDetails(exchange);
}

function parseSort(value: string | undefined): SwapsSort {
  return value === 'soonest' ? 'soonest' : 'best';
}

function getSearchFromParams(paramsObj: { [k: string]: string }): ISearchBy {
  const searchObj: ISearchBy = {
    city: paramsObj.city || '',
    state: paramsObj.state || '',
    startDate: utilService.deformatDate(paramsObj.startDate) || null,
    endDate: utilService.deformatDate(paramsObj.endDate) || null,
    // Opt-in: legacy mutualOnly=1 also enables this filter; absence means off.
    wantsMyCity: paramsObj.wantsMyCity === '1' || paramsObj.mutualOnly === '1',
    sort: parseSort(paramsObj.sort),
  };
  return searchObj;
}

function getDefaultSearch(): ISearchBy {
  return {
    city: '',
    state: '',
    startDate: null,
    endDate: null,
    wantsMyCity: false,
    sort: 'best',
  };
}

function getDefaultFilter() {
  return {
    labels: [],
  };
}

function formatDate(date: Date): string {
  const offset = date.getTimezoneOffset();
  const formatted = new Date(date.getTime() - offset * 60 * 1000);
  return `${formatted.toISOString().split('T')[0]} 12:00:00.000Z`;
}
